import React, { createContext, useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "axios";
import { PROXY } from "../lib/utils";
import { jwtDecode } from "jwt-decode";
import { googleLogout } from "@react-oauth/google";
import { isCanceledAxiosRequest, isHelpApiRequest } from "../lib/axiosErrors";
import { pendingCourseReturnPath } from "../lib/pendingCourseAction";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();

  // ✅ Helper function to check if token is expired
  const isTokenExpired = (token) => {
    if (!token) return true;

    try {
      const decoded = jwtDecode(token);
      const currentTime = Date.now() / 1000;
      return decoded.exp < currentTime;
    } catch (error) {
      console.error("Error decoding token:", error);
      return true;
    }
  };

  // ✅ Initialize auth state with token validation
  const [authState, setAuthState] = useState(() => {
    const storedUser = localStorage.getItem("user");
    const storedToken = localStorage.getItem("accessToken");

    // ✅ Check if token exists and is valid
    if (storedToken && isTokenExpired(storedToken)) {
      // Token is expired, clear everything
      localStorage.removeItem("user");
      localStorage.removeItem("accessToken");
      localStorage.removeItem("remember_me");

      return {
        user: null,
        accessToken: null,
        isLoading: false,
      };
    }

    return {
      user: storedUser ? JSON.parse(storedUser) : null,
      accessToken: storedToken || null,
      isLoading: false,
    };
  });

  const [rememberMe, setRememberMe] = useState(
    localStorage.getItem("remember_me") === "true"
  );

  const saveAuth = (user, accessToken) => {
    setAuthState({ user, accessToken, isLoading: false });
    localStorage.setItem("user", JSON.stringify(user));
    localStorage.setItem("accessToken", accessToken);
  };

  const googleLoginSuccess = (accessToken, user) => {
    saveAuth(user, accessToken);
    navigate(pendingCourseReturnPath() || "/dashboard", { replace: true });
  };

  const clearAuth = () => {
    setAuthState({ user: null, accessToken: null, isLoading: false });
    localStorage.removeItem("user");
    localStorage.removeItem("accessToken");
    localStorage.removeItem("remember_me");
  };

  const login = async (email, password, remember, captchaToken) => {
    setAuthState((prev) => ({ ...prev, isLoading: true }));
    try {
      const res = await axios.post(
        `${PROXY}/auth/login`,
        { email, password, captchaToken },
        { withCredentials: true }
      );
      const { accessToken, ...user } = res.data;
      saveAuth(user, accessToken);
      if (remember) localStorage.setItem("remember_me", "true");
      navigate(pendingCourseReturnPath() || "/dashboard");
    } catch (err) {
      setAuthState((prev) => ({ ...prev, isLoading: false }));
      throw err;
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${PROXY}/auth/logout`, {}, { withCredentials: true });
    } catch (err) {
      console.warn("Logout failed:", err);
    }

    // ✅ Also logout Google session if exists
    googleLogout();

    clearAuth();
    navigate("/login");
  };

  const refreshToken = async () => {
    try {
      const res = await axios.post(
        `${PROXY}/auth/refresh`,
        {},
        { withCredentials: true }
      );
      const { accessToken, ...user } = res.data;
      saveAuth(user, accessToken);
      return accessToken;
    } catch (err) {
      console.error("Token refresh failed:", err);
      clearAuth();
      navigate("/login");
      return null;
    }
  };

  // ✅ Set user manually (e.g., after profile update)
  const setUser = (user) => {
    setAuthState((prev) => {
      const updated = { ...prev, user };
      localStorage.setItem("user", JSON.stringify(user));
      return updated;
    });
  };

  // ✅ Refresh user from backend
  const refreshUser = async () => {
    try {
      const { data } = await axios.get(`${PROXY}/users/info`, {
        headers: { Authorization: `Bearer ${authState.accessToken}` },
      });
      setUser(data);
    } catch (err) {
      console.error("Failed to refresh user:", err.message);
      // ✅ If 401, token might be expired
      if (err.response?.status === 401) {
        clearAuth();
        navigate("/login");
      }
    }
  };

  // Studio access is granted by an administrator outside the learner session.
  // Rehydrate the profile on startup/token replacement so localStorage cannot
  // keep an authorized teacher hidden until they manually sign in again.
  useEffect(() => {
    if (authState.accessToken) {
      refreshUser();
    }
  }, [authState.accessToken]);

  useEffect(() => {
    const initialize = async () => {
      // ✅ Check token expiration before attempting refresh
      const storedToken = localStorage.getItem("accessToken");

      if (rememberMe) {
        if (!storedToken || isTokenExpired(storedToken)) {
          // Token expired or missing, try to refresh
          await refreshToken();
        }
      } else if (storedToken && isTokenExpired(storedToken)) {
        // Token expired and remember me is off, clear auth
        clearAuth();
      }
    };
    initialize();

    const interval = setInterval(() => {
      // ✅ Only refresh if token exists and is close to expiring
      const storedToken = localStorage.getItem("accessToken");
      if (rememberMe && storedToken && !isTokenExpired(storedToken)) {
        refreshToken();
      }
    }, 14 * 60 * 1000); // every 14 minutes

    return () => clearInterval(interval);
  }, [rememberMe]);

  useEffect(() => {
    const requestInterceptor = axios.interceptors.request.use(
      (config) => {
        // ✅ Check token expiration before each request
        if (authState.accessToken) {
          if (isTokenExpired(authState.accessToken)) {
            // Token expired, clear and redirect
            clearAuth();
            navigate("/login");
            return Promise.reject(new Error("Token expired"));
          }
          config.headers.Authorization = `Bearer ${authState.accessToken}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    const responseInterceptor = axios.interceptors.response.use(
      (res) => res,
      async (error) => {
        const originalRequest = error.config;

        if (isCanceledAxiosRequest(error) || !isHelpApiRequest(error)) {
          return Promise.reject(error);
        }

        if (!error.response) {
          toast.error("Network error");
          return Promise.reject(error);
        }

        if (error.response.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;
          const newToken = await refreshToken();
          if (newToken) {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return axios(originalRequest);
          }
        }

        if (
          error.response.status === 403 &&
          error.response.data?.auth === true
        ) {
          logout();
        }

        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, [authState.accessToken]);

  return (
    <AuthContext.Provider
      value={{
        authState,
        login,
        logout,
        googleLoginSuccess, // ✅ NOW DEFINED
        rememberMe,
        setRememberMe,
        refreshToken,
        setUser,
        refreshUser,
        setAuthState,
        saveAuth,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
